'use client';

/**
 * Drawing Canvas Component
 * Interactive canvas for drawing shaft diagrams with touch/mouse support
 * Features straight-line drawing for blueprint-style diagrams
 */

import React, { useRef, useState, useEffect } from 'react';
import type { DrawingData } from '@/types/project-specification.types';

interface DrawingCanvasProps {
  onDrawingChange: (data: DrawingData | null) => void;
}

interface Point {
  x: number;
  y: number;
}

interface Line {
  start: Point;
  end: Point;
  color: string;
  width: number;
}

interface Annotation {
  x: number;
  y: number;
  text: string;
  fontSize: number;
}

export default function DrawingCanvas({ onDrawingChange }: DrawingCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [lines, setLines] = useState<Line[]>([]);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [currentPoint, setCurrentPoint] = useState<Point | null>(null);
  const [lastEndPoint, setLastEndPoint] = useState<Point | null>(null);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [tool, setTool] = useState<'pen' | 'text'>('pen');
  const [color, setColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(2);
  const [showGrid, setShowGrid] = useState(true);
  const [annotationText, setAnnotationText] = useState('');
  const [annotationPosition, setAnnotationPosition] = useState<Point | null>(null);
  const [snapToLast, setSnapToLast] = useState(true);
  const snapDistance = 15; // pixels within which to snap to last point

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw grid if enabled
    if (showGrid) {
      drawGrid(ctx, canvas.width, canvas.height);
    }

    // Draw all completed lines
    lines.forEach((line) => {
      drawLine(ctx, line.start, line.end, line.color, line.width);
    });

    // Draw preview line while dragging
    if (isDrawing && startPoint && currentPoint) {
      drawLine(ctx, startPoint, currentPoint, color, lineWidth, true);
    }

    // Highlight last end point if snap is enabled
    if (snapToLast && lastEndPoint && !isDrawing) {
      ctx.fillStyle = '#3b82f6';
      ctx.beginPath();
      ctx.arc(lastEndPoint.x, lastEndPoint.y, 6, 0, 2 * Math.PI);
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Draw annotations
    annotations.forEach((annotation) => {
      drawAnnotation(ctx, annotation);
    });

    // Update parent component
    if (lines.length > 0 || annotations.length > 0) {
      const drawingData: DrawingData = {
        strokes: lines.map(line => ({
          points: [line.start, line.end],
          color: line.color,
          width: line.width,
        })),
        annotations,
        dimensions: {
          width: canvas.width,
          height: canvas.height,
        },
      };
      onDrawingChange(drawingData);
    }
  }, [lines, isDrawing, startPoint, currentPoint, lastEndPoint, annotations, showGrid, color, lineWidth, snapToLast, onDrawingChange]);

  const drawGrid = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 0.5;

    const gridSize = 20;

    // Vertical lines
    for (let x = 0; x <= width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }

    // Horizontal lines
    for (let y = 0; y <= height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  };

  const drawLine = (
    ctx: CanvasRenderingContext2D,
    start: Point,
    end: Point,
    strokeColor: string,
    strokeWidth: number,
    isPreview = false
  ) => {
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (isPreview) {
      ctx.setLineDash([5, 5]);
      ctx.globalAlpha = 0.6;
    } else {
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }

    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
  };

  const drawAnnotation = (ctx: CanvasRenderingContext2D, annotation: Annotation) => {
    ctx.fillStyle = '#dc2626';
    ctx.font = `${annotation.fontSize}px Arial`;
    ctx.fillText(annotation.text, annotation.x, annotation.y);
  };

  const getCoordinates = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    
    // Account for canvas scaling - canvas internal size vs displayed size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if ('touches' in e) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const shouldSnapToLastPoint = (point: Point): boolean => {
    if (!snapToLast || !lastEndPoint) return false;
    
    const distance = Math.sqrt(
      Math.pow(point.x - lastEndPoint.x, 2) + Math.pow(point.y - lastEndPoint.y, 2)
    );
    
    return distance <= snapDistance;
  };

  const handleStart = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    let point = getCoordinates(e);

    if (tool === 'text') {
      setAnnotationPosition(point);
      return;
    }

    // Snap to last end point if close enough
    if (shouldSnapToLastPoint(point)) {
      point = lastEndPoint!;
    }

    setIsDrawing(true);
    setStartPoint(point);
    setCurrentPoint(point);
  };

  const handleMove = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawing || tool === 'text' || !startPoint) return;

    let point = getCoordinates(e);
    setCurrentPoint(point);
  };

  const handleEnd = () => {
    if (isDrawing && startPoint && currentPoint) {
      // Only add line if there's meaningful distance
      const distance = Math.sqrt(
        Math.pow(currentPoint.x - startPoint.x, 2) + Math.pow(currentPoint.y - startPoint.y, 2)
      );

      if (distance > 5) { // Minimum 5 pixels to avoid accidental dots
        const newLine: Line = {
          start: startPoint,
          end: currentPoint,
          color,
          width: lineWidth,
        };

        setLines((prev) => [...prev, newLine]);
        setLastEndPoint(currentPoint);
      }
    }

    setIsDrawing(false);
    setStartPoint(null);
    setCurrentPoint(null);
  };

  const handleAddAnnotation = () => {
    if (annotationPosition && annotationText.trim()) {
      setAnnotations((prev) => [
        ...prev,
        {
          ...annotationPosition,
          text: annotationText,
          fontSize: 14,
        },
      ]);
      setAnnotationText('');
      setAnnotationPosition(null);
    }
  };

  const handleClear = () => {
    setLines([]);
    setAnnotations([]);
    setLastEndPoint(null);
    onDrawingChange(null);
  };

  const handleUndo = () => {
    if (lines.length > 0) {
      const newLines = lines.slice(0, -1);
      setLines(newLines);
      
      // Update last end point to the previous line's end
      if (newLines.length > 0) {
        setLastEndPoint(newLines[newLines.length - 1].end);
      } else {
        setLastEndPoint(null);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        {/* Tool Selection */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setTool('pen')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              tool === 'pen' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            📏 Draw
          </button>
          <button
            type="button"
            onClick={() => setTool('text')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              tool === 'text' ? 'bg-red-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            📝 Annotate
          </button>
        </div>

        {/* Color Picker */}
        {tool === 'pen' && (
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Color:</label>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
            />
          </div>
        )}

        {/* Line Width */}
        {tool === 'pen' && (
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Width:</label>
            <input
              type="range"
              min="1"
              max="10"
              value={lineWidth}
              onChange={(e) => setLineWidth(parseInt(e.target.value))}
              className="w-24"
            />
            <span className="text-sm text-gray-600 w-8">{lineWidth}px</span>
          </div>
        )}

        {/* Grid Toggle */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showGrid}
            onChange={(e) => setShowGrid(e.target.checked)}
            className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
          />
          <span className="text-sm font-medium text-gray-700">Show Grid</span>
        </label>

        {/* Snap Toggle */}
        {tool === 'pen' && (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={snapToLast}
              onChange={(e) => setSnapToLast(e.target.checked)}
              className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
            />
            <span className="text-sm font-medium text-gray-700">🧲 Auto-Connect</span>
          </label>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={handleUndo}
            disabled={lines.length === 0}
            className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            ↶ Undo
          </button>
          <button
            type="button"
            onClick={handleClear}
            className="px-4 py-2 bg-white text-red-600 border border-red-300 rounded-lg hover:bg-red-50 font-medium"
          >
            🗑️ Clear
          </button>
        </div>
      </div>

      {/* Annotation Input */}
      {annotationPosition && (
        <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <input
            type="text"
            value={annotationText}
            onChange={(e) => setAnnotationText(e.target.value)}
            placeholder="Enter dimension or note..."
            className="flex-1 px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoFocus
          />
          <button
            type="button"
            onClick={handleAddAnnotation}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => {
              setAnnotationPosition(null);
              setAnnotationText('');
            }}
            className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Canvas */}
      <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={800}
          height={600}
          onMouseDown={handleStart}
          onMouseMove={handleMove}
          onMouseUp={handleEnd}
          onMouseLeave={handleEnd}
          onTouchStart={handleStart}
          onTouchMove={handleMove}
          onTouchEnd={handleEnd}
          className="w-full touch-none cursor-crosshair"
          style={{ touchAction: 'none' }}
        />
      </div>

      {/* Instructions */}
      <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
        <p className="font-semibold mb-2">📐 Blueprint Drawing Mode:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Straight Lines:</strong> Click/tap and drag - releases as perfectly straight line</li>
          <li><strong>Auto-Connect:</strong> Blue dot shows last endpoint - start near it to snap and continue</li>
          <li><strong>Preview:</strong> Dashed line shows where your straight line will be drawn</li>
          <li><strong>Annotate:</strong> Add measurements and notes to your blueprint</li>
          <li><strong>Grid:</strong> Enable grid for precise alignment and measurements</li>
          <li><strong>Undo:</strong> Remove the last line drawn</li>
        </ul>
      </div>
    </div>
  );
}